/**
 * A time axis's span and its tick and tooltip formats (2026-09-28), apart from the chart so the chart's file exports
 * only components. Pure.
 */
import { format } from "date-fns";

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

/** The rows' own first and last times, as epoch ms; one point, or none, widens by an hour each way. Pure. */
export function dataSpan(rows: ReadonlyArray<{ ts: number }>): [number, number] {
  let lo = Infinity;
  let hi = -Infinity;
  for (const r of rows) {
    if (r.ts < lo) lo = r.ts;
    if (r.ts > hi) hi = r.ts;
  }
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return [0, HOUR_MS];
  return hi > lo ? [lo, hi] : [lo - HOUR_MS, hi + HOUR_MS];
}

/**
 * How a time axis and its tooltip read at a span of milliseconds: a day and a half or less reads the time of day, a
 * week the day and the time, half a year the day, anything longer the month. The tooltip always names the day, and
 * the time too while the points can fall within a day (a span of a month and a half or less). Pure.
 */
export function timeFormats(spanMs: number): { tick: string; tooltip: string } {
  if (spanMs <= 36 * HOUR_MS) return { tick: "HH:mm", tooltip: "d MMM yyyy, HH:mm" };
  if (spanMs <= 8 * DAY_MS) return { tick: "d MMM HH:mm", tooltip: "d MMM yyyy, HH:mm" };
  if (spanMs <= 190 * DAY_MS) {
    return { tick: "d MMM", tooltip: spanMs <= 45 * DAY_MS ? "d MMM yyyy, HH:mm" : "d MMM yyyy" };
  }
  return { tick: "MMM yyyy", tooltip: "d MMM yyyy" };
}

/** An epoch-ms time in a date-fns format; "" for a time that is not one. */
export function formatTime(ms: number, pattern: string): string {
  return Number.isFinite(ms) ? format(new Date(ms), pattern) : "";
}

const MINUTE_MS = 60_000;
/** The tick steps up to a week, shortest first; longer spans step in whole months (MONTH_STEPS). */
const FIXED_STEPS = [1, 2, 5, 10, 15, 30]
  .map((m) => m * MINUTE_MS)
  .concat([1, 2, 3, 6, 12].map((h) => h * HOUR_MS), [DAY_MS, 2 * DAY_MS, 7 * DAY_MS]);
const MONTH_STEPS = [1, 2, 3, 6, 12];

/**
 * The ticks of a time axis over [from, to] (2026-10-30): round times on the local clock (whole minutes, hours, days or
 * months), at most `most` of them, spread over the whole span. Recharts left to itself put a time axis's ticks at the
 * readings' own times, so a lookback whose readings start late (1h with readings in its last half hour) had no tick
 * over its empty part. Hours and days are counted on the local clock, so an IST axis reads 23:00, not 22:30. Pure.
 */
export function timeTicks(from: number, to: number, most = 10): number[] {
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return [];
  const span = to - from;
  const out: number[] = [];
  // A step that fits the span `most - 1` times gives at most `most` ticks, however the span lies against it.
  const step = FIXED_STEPS.find((s) => span / s <= most - 1);
  if (step !== undefined) {
    const d = new Date(from);
    if (step < HOUR_MS) {
      const m = step / MINUTE_MS;
      d.setMinutes(Math.floor(d.getMinutes() / m) * m, 0, 0);
    } else if (step < DAY_MS) {
      const h = step / HOUR_MS;
      d.setHours(Math.floor(d.getHours() / h) * h, 0, 0, 0);
    } else {
      d.setHours(0, 0, 0, 0);
    }
    while (d.getTime() <= to) {
      if (d.getTime() >= from) out.push(d.getTime());
      if (step < DAY_MS) d.setTime(d.getTime() + step);
      else d.setDate(d.getDate() + step / DAY_MS);
    }
    return out;
  }
  const months =
    MONTH_STEPS.find((m) => span / (m * 31 * DAY_MS) <= most - 1) ?? 12 * Math.ceil(span / ((most - 1) * 365 * DAY_MS));
  const d = new Date(from);
  d.setHours(0, 0, 0, 0);
  d.setDate(1);
  if (months % 12 === 0) d.setFullYear(Math.floor(d.getFullYear() / (months / 12)) * (months / 12), 0, 1);
  else d.setMonth(Math.floor(d.getMonth() / months) * months);
  while (d.getTime() <= to) {
    if (d.getTime() >= from) out.push(d.getTime());
    d.setMonth(d.getMonth() + months);
  }
  return out;
}

const UNIT_MS: Record<string, number> = {
  second: 1_000,
  sec: 1_000,
  minute: MINUTE_MS,
  min: MINUTE_MS,
  hour: HOUR_MS,
  hr: HOUR_MS,
  day: DAY_MS,
  week: 7 * DAY_MS,
};

/**
 * A bucket's width in ms, from the interval a values read answers with: a count of a unit ("1 minute", "15 minutes",
 * "1 day") or Go's form ("5m0s", "1h0m0s"). undefined for "raw" (each reading as it is), a missing interval or one that
 * does not read. Pure.
 */
export function bucketWidthMs(interval: string | undefined): number | undefined {
  const s = interval?.trim().toLowerCase();
  if (!s || s === "raw") return undefined;
  const words = /^(\d+(?:\.\d+)?)\s+([a-z]+?)s?$/.exec(s);
  if (words) {
    const unit = UNIT_MS[words[2]];
    return unit ? Number(words[1]) * unit : undefined;
  }
  const go = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+(?:\.\d+)?)s)?$/.exec(s);
  if (!go || !(go[1] || go[2] || go[3])) return undefined;
  const ms = (Number(go[1] ?? 0) * 3600 + Number(go[2] ?? 0) * 60 + Number(go[3] ?? 0)) * 1000;
  return ms > 0 ? ms : undefined;
}

/**
 * A bucketed series' rows with a null row in the middle of every stretch of more than two buckets that has no point:
 * no reading was taken there, so the line breaks instead of drawing a slope across it (2026-10-30). Rows are in time
 * order. Raw readings are never broken (no `bucketMs`): a tag that reports only on a change can be quiet for hours. Pure.
 */
export function withGapBreaks<R extends { ts: number }>(rows: readonly R[], bucketMs: number | undefined, gap: (ts: number) => R): R[] {
  if (!bucketMs || !(bucketMs > 0) || rows.length < 2) return [...rows];
  const out: R[] = [rows[0]];
  for (let i = 1; i < rows.length; i++) {
    const prev = rows[i - 1].ts;
    if (rows[i].ts - prev > 2 * bucketMs) out.push(gap(prev + (rows[i].ts - prev) / 2));
    out.push(rows[i]);
  }
  return out;
}
